// Periods — the ledger's months. A closed month refuses new entries; reopening
// is logged with a reason. Each month shows a short checklist before closing.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useBooksAccess } from '../../contexts/AuthContext';
import { ConfirmButton, Empty, ErrorNote, Field, Note, Section } from '../../components/ui';
import { dateTime, dollars, monthName } from '../../lib/format';
import { assets, bank, journal, periods, reports } from '../../lib/ledger/api';
import type { Overview, Period } from '../../lib/ledger/types';
import { ReadOnlyNote, monthEndOf, monthStart } from './shared';

type MonthLine = { first: string; period: Period | null };

/** Every first-of-month from `from` through `to`, newest first. */
function monthsBetween(from: string, to: string): string[] {
  const out: string[] = [];
  const [fy, fm] = from.split('-').map(Number);
  const [ty, tm] = to.split('-').map(Number);
  let y = ty, m = tm;
  while (y > fy || (y === fy && m >= fm)) {
    out.push(`${y}-${String(m).padStart(2, '0')}-01`);
    m -= 1;
    if (m === 0) { m = 12; y -= 1; }
  }
  return out;
}

export function PeriodsTab() {
  const { canEdit } = useBooksAccess();
  const [rows, setRows] = useState<Period[] | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [selected, setSelected] = useState<string>(monthStart());
  const [done, setDone] = useState<string | null>(null);
  const [actErr, setActErr] = useState<unknown>(null);
  const [reason, setReason] = useState('');
  const [tick, setTick] = useState(0);

  // Checklist data for the selected month.
  const [ov, setOv] = useState<Overview | null>(null);
  const [forReview, setForReview] = useState<number | null>(null);
  const [appleRow, setAppleRow] = useState<{ closed_at: string | null } | null | undefined>(undefined);
  const [deprCount, setDeprCount] = useState<number | null>(null);
  const [entryCount, setEntryCount] = useState<number | null>(null);
  const [checkErr, setCheckErr] = useState<unknown>(null);

  useEffect(() => {
    let alive = true;
    setErr(null);
    periods.list()
      .then((r) => alive && setRows(r ?? []))
      .catch((e) => alive && setErr(e));
    return () => { alive = false; };
  }, [tick]);

  const loadChecks = useCallback(async (first: string) => {
    setCheckErr(null);
    const [o, fr, ap, runs, j] = await Promise.allSettled([
      reports.overview(),
      bank.forReviewCount(),
      supabase.from('books_months').select('closed_at').eq('month', first).maybeSingle(),
      assets.runs(),
      journal.page({ from: first, to: monthEndOf(first), limit: 1 }),
    ]);
    if (o.status === 'fulfilled') setOv(o.value); else setCheckErr(o.reason);
    setForReview(fr.status === 'fulfilled' ? fr.value : null);
    if (ap.status === 'fulfilled' && !ap.value.error) setAppleRow((ap.value.data as { closed_at: string | null } | null) ?? null);
    else setAppleRow(null);
    setDeprCount(runs.status === 'fulfilled' ? runs.value.filter((r) => r.month.slice(0, 7) === first.slice(0, 7)).length : null);
    setEntryCount(j.status === 'fulfilled' ? j.value.total : null);
  }, []);

  useEffect(() => {
    setAppleRow(undefined);
    void loadChecks(selected);
  }, [selected, loadChecks, tick]);

  const months = useMemo<MonthLine[]>(() => {
    const byMonth = new Map((rows ?? []).map((p) => [p.month.slice(0, 10), p]));
    const thisMonth = monthStart();
    const earliestRow = (rows ?? []).map((p) => p.month.slice(0, 10)).sort()[0];
    const janThisYear = `${thisMonth.slice(0, 4)}-01-01`;
    const from = earliestRow && earliestRow < janThisYear ? earliestRow : janThisYear;
    return monthsBetween(from, thisMonth).map((first) => ({ first, period: byMonth.get(first) ?? null }));
  }, [rows]);

  const current = months.find((m) => m.first === selected) ?? { first: selected, period: null };
  const isClosed = current.period?.status === 'closed';

  async function act(label: string, fn: () => Promise<unknown>) {
    setActErr(null);
    setDone(null);
    try {
      const r = await fn();
      setDone(label + summarize(r));
      setReason('');
      setTick((t) => t + 1);
    } catch (e) {
      setActErr(e);
    }
  }

  return (
    <>
      <p className="lp-sub" style={{ margin: '0 0 12px' }}>
        A closed month refuses new entries. Reopening is allowed and is logged with your reason.
      </p>
      {!canEdit && <ReadOnlyNote />}
      <ErrorNote error={err} notConfigured="The ledger is not set up yet." />
      {done && <Note tone="good">{done}</Note>}
      <ErrorNote error={actErr} />

      <Section
        title={monthName(current.first)}
        right={<span className={isClosed ? 'lp-tag lp-tag--good' : 'lp-tag lp-tag--warn'}>{isClosed ? 'Closed' : 'Open'}</span>}
      >
        <div className="lp-panel">
          {current.period?.closed_at && (
            <p className="lp-field-hint" style={{ marginTop: 0 }}>
              Closed {dateTime(current.period.closed_at)}
              {current.period.reopened_at ? ` · reopened ${dateTime(current.period.reopened_at)}` : ''}
            </p>
          )}
          <h3 className="lp-item-title" style={{ marginTop: 0 }}>Before closing</h3>
          <ErrorNote error={checkErr} />
          <ul className="lp-list">
            <Check
              ok={forReview === 0}
              pending={forReview === null}
              text={forReview === null ? 'Bank items for review: could not load.' : forReview === 0 ? 'No bank items waiting for review.' : `${forReview} bank ${forReview === 1 ? 'item is' : 'items are'} waiting for review (all months).`}
            />
            <Check
              ok={Boolean(appleRow?.closed_at)}
              pending={appleRow === undefined}
              text={appleRow === undefined ? 'Apple month: loading…' : appleRow === null ? 'No Apple numbers entered for this month. Fine if Apple had nothing to report.' : appleRow.closed_at ? `Apple month closed ${dateTime(appleRow.closed_at)}.` : 'Apple month is not closed yet. Close it under Apple first so its entries post.'}
            />
            <Check
              ok={(ov?.assets_in_service ?? 0) === 0 || (deprCount ?? 0) > 0}
              pending={deprCount === null}
              text={deprCount === null ? 'Depreciation: could not load.' : (ov?.assets_in_service ?? 0) === 0 ? 'No assets in service, so no depreciation this month.' : deprCount > 0 ? `Depreciation posted for ${deprCount} ${deprCount === 1 ? 'asset' : 'assets'} this month.` : `${ov?.assets_in_service} ${ov?.assets_in_service === 1 ? 'asset is' : 'assets are'} in service and depreciation has not been run for this month. Run it under Assets before closing.`}
            />
            <Check
              ok={entryCount !== null}
              pending={entryCount === null}
              text={entryCount === null ? 'Entries: could not count.' : entryCount === 0 ? 'No entries this month yet.' : `${entryCount} ${entryCount === 1 ? 'entry' : 'entries'} this month.`}
            />
          </ul>
          <p className="lp-field-hint">Depreciation is straight-line and posts from the Assets tab, one month at a time.</p>

          <div className="lp-actions">
            {!isClosed ? (
              <ConfirmButton
                label={`Close ${monthName(current.first)}`}
                tone="primary"
                disabled={!canEdit}
                sentence={`Closing ${monthName(current.first)} stops new entries in it. You can reopen it later with a reason.`}
                yes="Yes, close it"
                onConfirm={() => act(`${monthName(current.first)} is closed.`, () => periods.close(current.first))}
              />
            ) : (
              <div className="lp-form" style={{ width: '100%' }}>
                <Field label="Reason to reopen" hint="Required. It goes in the log.">
                  <input className="lp-inp" value={reason} disabled={!canEdit} onChange={(e) => setReason(e.target.value)} />
                </Field>
                <ConfirmButton
                  label={`Reopen ${monthName(current.first)}`}
                  disabled={!canEdit || !reason.trim()}
                  sentence={`Reopening ${monthName(current.first)} lets entries be added or changed again. Your reason is logged.`}
                  yes="Yes, reopen it"
                  onConfirm={() => act(`${monthName(current.first)} is open again.`, () => periods.reopen(current.first, reason.trim()))}
                />
              </div>
            )}
          </div>
        </div>
      </Section>

      <Section title="All months">
        {rows === null && !err ? <Empty>Loading…</Empty> : (
          <div className="lp-tbl-wrap">
            <table className="lp-tbl">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Status</th>
                  <th>Closed</th>
                  <th>Reopened</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {months.map(({ first, period }) => {
                  const closed = period?.status === 'closed';
                  const on = first === selected;
                  return (
                    <tr key={first} className={on ? '' : closed ? '' : 'quiet'} style={on ? { background: 'var(--moss-wash)' } : undefined}>
                      <td><strong>{monthName(first)}</strong></td>
                      <td><span className={closed ? 'lp-tag lp-tag--good' : 'lp-tag lp-tag--warn'}>{closed ? 'Closed' : 'Open'}</span></td>
                      <td>{period?.closed_at ? dateTime(period.closed_at) : '—'}</td>
                      <td>{period?.reopened_at ? dateTime(period.reopened_at) : '—'}</td>
                      <td>
                        <button type="button" className="lp-btn lp-btn--quiet lp-btn--small" aria-current={on ? 'true' : undefined} onClick={() => setSelected(first)}>
                          {on ? 'Selected' : 'Select'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="lp-field-hint" style={{ marginTop: 10 }}>A month with no row is open. Rows appear the first time a month is closed.</p>
      </Section>
    </>
  );
}

function Check({ ok, pending, text }: { ok: boolean; pending?: boolean; text: string }) {
  const cls = pending ? 'lp-tag' : ok ? 'lp-tag lp-tag--good' : 'lp-tag lp-tag--warn';
  const word = pending ? '…' : ok ? 'Ready' : 'Check';
  return (
    <li className="lp-item" style={{ gridTemplateColumns: 'max-content 1fr', alignItems: 'start' }}>
      <span className={cls}>{word}</span>
      <div className="lp-item-line" style={{ marginTop: 0 }}>{text}</div>
    </li>
  );
}

/** " Net income $1,234.00 · 12 entries." from a close/reopen summary, or nothing. */
function summarize(r: unknown): string {
  if (!r || typeof r !== 'object') return '';
  const o = r as Record<string, unknown>;
  const bits: string[] = [];
  if (typeof o.net_income_cents === 'number') bits.push(`Net income ${dollars(o.net_income_cents)}`);
  if (typeof o.entries === 'number') bits.push(`${o.entries} ${o.entries === 1 ? 'entry' : 'entries'}`);
  if (typeof o.for_review === 'number' && o.for_review > 0) bits.push(`${o.for_review} bank ${o.for_review === 1 ? 'item' : 'items'} still for review`);
  return bits.length ? ` ${bits.join(' · ')}.` : '';
}
